/**
 * Mock email data — 18 realistic healthcare practice emails.
 *
 * Each email follows the unified signal + email data model:
 * identity, participants, content, timestamps, state, AI classification, queue integration.
 */

const now = new Date();
const hoursAgo = (h) => new Date(now - h * 3600_000).toISOString();
const daysAgo = (d) => new Date(now - d * 86400_000).toISOString();

export const MOCK_EMAILS = [
  {
    id: 'em-001',
    threadId: 'thread-001',
    from: { name: 'Dr. Sarah Smith', email: 'sarah.smith@sunridgeclinic.org' },
    to: [{ name: 'Practice Manager', email: 'pm@sunridgeclinic.org' }],
    cc: [{ name: 'Nurse Manager', email: 'nm@sunridgeclinic.org' }],
    subject: 'RE: Staffing Coverage — Weekend Shifts',
    preview: 'Need 2 RNs for the weekend shift. Float pool request pending confirmation from HR.',
    bodyHtml: '<p>Hi team,</p><p>We need <strong>2 RNs</strong> for the weekend shift (Sat–Sun). Float pool request has been submitted but HR hasn\'t confirmed yet. If we don\'t hear back by <strong>Friday 5pm</strong>, we need to activate the on-call roster.</p><p>Current coverage: 3/5 slots filled. Dr. Patel is covering the physician side.</p><p>— Sarah</p>',
    bodyText: 'Hi team, We need 2 RNs for the weekend shift (Sat–Sun). Float pool request has been submitted but HR hasn\'t confirmed yet.',
    attachments: [{ id: 'att-001', filename: 'weekend-schedule.xlsx', mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', size: 24576 }],
    receivedAt: hoursAgo(3),
    isRead: false,
    isStarred: true,
    folder: 'inbox',
    labels: ['follow-up'],
    primaryDomain: 'staffing',
    domains: [{ domain: 'staffing', confidence: 94 }, { domain: 'access', confidence: 18 }],
    urgency: 4,
    classificationConfidence: 94,
    entities: [
      { type: 'date', value: 'Friday 5pm', span: [142, 152] },
      { type: 'person', value: '2 RNs', span: [28, 33] },
      { type: 'person', value: 'Dr. Patel', span: [220, 229] },
    ],
    threadSummary: '3 messages about weekend staffing shortage. Key action: need 2 RNs confirmed by Friday 5pm or activate on-call roster.',
    queueStatus: 'none',
    priority: 'high',
    scheduledDate: null,
    boardSwimlane: null,
    reclassifiedDomain: null,
    threadMessages: [
      { id: 'msg-001a', from: { name: 'Dr. Sarah Smith', email: 'sarah.smith@sunridgeclinic.org' }, sentAt: hoursAgo(3), bodyHtml: '<p>Hi team,</p><p>We need <strong>2 RNs</strong> for the weekend shift (Sat–Sun). Float pool request has been submitted but HR hasn\'t confirmed yet. If we don\'t hear back by <strong>Friday 5pm</strong>, we need to activate the on-call roster.</p><p>Current coverage: 3/5 slots filled. Dr. Patel is covering the physician side.</p><p>— Sarah</p>', bodyText: 'Hi team, We need 2 RNs for the weekend shift...', attachments: [{ id: 'att-001', filename: 'weekend-schedule.xlsx', mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', size: 24576 }] },
      { id: 'msg-001b', from: { name: 'Practice Manager', email: 'pm@sunridgeclinic.org' }, sentAt: hoursAgo(2), bodyHtml: '<p>Thanks Sarah. I\'ll follow up with HR now and report back within the hour.</p>', bodyText: 'Thanks Sarah. I\'ll follow up with HR now.', attachments: [] },
      { id: 'msg-001c', from: { name: 'Nurse Manager', email: 'nm@sunridgeclinic.org' }, sentAt: hoursAgo(1), bodyHtml: '<p>I have one float pool RN tentatively confirmed — Maria Chen. Still need one more.</p>', bodyText: 'I have one float pool RN tentatively confirmed — Maria Chen.', attachments: [] },
    ],
    threadMessageCount: 3,
  },
  {
    id: 'em-002',
    threadId: 'thread-002',
    from: { name: 'Lab Results Inc.', email: 'reports@labresultsinc.com' },
    to: [{ name: 'Practice Manager', email: 'pm@sunridgeclinic.org' }],
    cc: [],
    subject: 'Q1 Quality Metrics Report — Action Required',
    preview: 'Your Q1 quality metrics report is ready. A1C control rate has dropped to 68%, below the 75% target.',
    bodyHtml: '<p>Dear Practice Manager,</p><p>Your Q1 quality metrics report is now available. Key findings:</p><ul><li>A1C control rate: <strong>68%</strong> (target: 75%) — <em>action required</em></li><li>HEDIS compliance: 91% — on track</li><li>Diabetes screening completion: 82%</li></ul><p>The A1C decline is primarily in the 40-60 age cohort. We recommend scheduling outreach for the 47 patients with overdue labs.</p><p>Full report attached.</p>',
    bodyText: 'Your Q1 quality metrics report is ready. A1C control rate has dropped to 68%, below the 75% target.',
    attachments: [{ id: 'att-002', filename: 'Q1-Quality-Report.pdf', mimeType: 'application/pdf', size: 1048576 }],
    receivedAt: daysAgo(1),
    isRead: false,
    isStarred: false,
    folder: 'inbox',
    labels: [],
    primaryDomain: 'quality',
    domains: [{ domain: 'quality', confidence: 97 }, { domain: 'health', confidence: 32 }],
    urgency: 3,
    classificationConfidence: 97,
    entities: [
      { type: 'amount', value: '68%', span: [90, 93] },
      { type: 'amount', value: '47 patients', span: [210, 221] },
    ],
    threadSummary: 'Q1 quality report: A1C control rate dropped to 68% (target 75%). 47 patients need outreach for overdue labs.',
    queueStatus: 'none',
    priority: 'high',
    scheduledDate: null,
    boardSwimlane: null,
    reclassifiedDomain: null,
    threadMessages: [
      { id: 'msg-002a', from: { name: 'Lab Results Inc.', email: 'reports@labresultsinc.com' }, sentAt: daysAgo(1), bodyHtml: '<p>Dear Practice Manager,</p><p>Your Q1 quality metrics report is now available...</p>', bodyText: 'Your Q1 quality metrics report is ready.', attachments: [{ id: 'att-002', filename: 'Q1-Quality-Report.pdf', mimeType: 'application/pdf', size: 1048576 }] },
    ],
    threadMessageCount: 1,
  },
  {
    id: 'em-003',
    threadId: 'thread-003',
    from: { name: 'BlueCross Claims', email: 'claims@bcbs-portal.com' },
    to: [{ name: 'Billing Department', email: 'billing@sunridgeclinic.org' }],
    cc: [{ name: 'Practice Manager', email: 'pm@sunridgeclinic.org' }],
    subject: 'Claim Denial Notice — Claim #4821-RX',
    preview: 'Claim #4821-RX has been denied. Reason: Missing prior authorization for procedure code 99214.',
    bodyHtml: '<p>This is an automated notice regarding claim <strong>#4821-RX</strong>.</p><p><strong>Status:</strong> DENIED</p><p><strong>Reason:</strong> Missing prior authorization for procedure code 99214</p><p><strong>Patient:</strong> [REDACTED]</p><p><strong>Date of Service:</strong> March 15, 2026</p><p><strong>Amount:</strong> $285.00</p><p>You have 30 days from this notice to submit an appeal with the required authorization documentation.</p>',
    bodyText: 'Claim #4821-RX has been denied. Reason: Missing prior authorization for procedure code 99214.',
    attachments: [{ id: 'att-003', filename: 'denial-notice-4821.pdf', mimeType: 'application/pdf', size: 52000 }],
    receivedAt: daysAgo(4),
    isRead: true,
    isStarred: false,
    folder: 'inbox',
    labels: [],
    primaryDomain: 'finance',
    domains: [{ domain: 'finance', confidence: 99 }],
    urgency: 2,
    classificationConfidence: 99,
    entities: [
      { type: 'amount', value: '$285.00', span: [180, 187] },
      { type: 'date', value: 'March 15, 2026', span: [155, 169] },
    ],
    threadSummary: 'Claim #4821-RX denied for missing prior auth. $285 at stake. Appeal deadline: 30 days.',
    queueStatus: 'none',
    priority: 'medium',
    scheduledDate: null,
    boardSwimlane: null,
    reclassifiedDomain: null,
    threadMessages: [
      { id: 'msg-003a', from: { name: 'BlueCross Claims', email: 'claims@bcbs-portal.com' }, sentAt: daysAgo(4), bodyHtml: '<p>This is an automated notice regarding claim <strong>#4821-RX</strong>...</p>', bodyText: 'Claim #4821-RX denied.', attachments: [{ id: 'att-003', filename: 'denial-notice-4821.pdf', mimeType: 'application/pdf', size: 52000 }] },
      { id: 'msg-003b', from: { name: 'Billing Department', email: 'billing@sunridgeclinic.org' }, sentAt: daysAgo(3), bodyHtml: '<p>I\'ve pulled the auth form. It was submitted late — 2 days after the service date. I\'ll prepare the appeal today.</p>', bodyText: 'Auth form submitted late. Preparing appeal.', attachments: [] },
    ],
    threadMessageCount: 2,
  },
  {
    id: 'em-004',
    threadId: 'thread-004',
    from: { name: 'Access Team', email: 'access@sunridgeclinic.org' },
    to: [{ name: 'Practice Manager', email: 'pm@sunridgeclinic.org' }],
    cc: [],
    subject: 'URGENT: Patient Wait Times Exceeding Target',
    preview: 'Average wait time hit 42 minutes today. Target is 15 minutes. Three providers running behind.',
    bodyHtml: '<p><strong>ALERT: Wait Time Threshold Exceeded</strong></p><p>Current average wait: <strong>42 minutes</strong> (target: 15 min)</p><p>Contributing factors:</p><ul><li>Dr. Reynolds running 35 min behind — complex case overrun</li><li>Dr. Chen running 20 min behind — walk-in volume spike</li><li>Front desk staffing at 2/3 (Julie called out)</li></ul><p>Suggested actions:</p><ol><li>Redirect walk-ins to Dr. Patel (12 min wait)</li><li>Open overflow exam room C</li><li>Consider activating the urgent care redirect protocol</li></ol>',
    bodyText: 'Average wait time hit 42 minutes today. Target is 15 minutes.',
    attachments: [],
    receivedAt: hoursAgo(2),
    isRead: false,
    isStarred: true,
    folder: 'inbox',
    labels: ['urgent'],
    primaryDomain: 'access',
    domains: [{ domain: 'access', confidence: 96 }, { domain: 'staffing', confidence: 28 }],
    urgency: 5,
    classificationConfidence: 96,
    entities: [
      { type: 'amount', value: '42 minutes', span: [35, 45] },
      { type: 'person', value: 'Dr. Reynolds', span: [80, 92] },
      { type: 'person', value: 'Dr. Chen', span: [120, 128] },
      { type: 'person', value: 'Dr. Patel', span: [200, 209] },
    ],
    threadSummary: 'Wait times at 42 min (target: 15). Three providers behind. Suggested: redirect walk-ins, open overflow room, activate urgent care protocol.',
    queueStatus: 'none',
    priority: 'critical',
    scheduledDate: null,
    boardSwimlane: null,
    reclassifiedDomain: null,
    threadMessages: [
      { id: 'msg-004a', from: { name: 'Access Team', email: 'access@sunridgeclinic.org' }, sentAt: hoursAgo(2), bodyHtml: '<p><strong>ALERT</strong></p><p>Current average wait: <strong>42 minutes</strong>...</p>', bodyText: 'Average wait time hit 42 minutes.', attachments: [] },
      { id: 'msg-004b', from: { name: 'Practice Manager', email: 'pm@sunridgeclinic.org' }, sentAt: hoursAgo(1.5), bodyHtml: '<p>Opening overflow room C now. Redirecting walk-ins to Dr. Patel. Will check back in 30 min.</p>', bodyText: 'Opening overflow room C. Redirecting walk-ins.', attachments: [] },
      { id: 'msg-004c', from: { name: 'Front Desk', email: 'frontdesk@sunridgeclinic.org' }, sentAt: hoursAgo(1), bodyHtml: '<p>Room C is open. Current walk-in queue redirected. Wait time dropping — now at 28 min.</p>', bodyText: 'Room C open. Wait time dropping — 28 min.', attachments: [] },
      { id: 'msg-004d', from: { name: 'Access Team', email: 'access@sunridgeclinic.org' }, sentAt: hoursAgo(0.5), bodyHtml: '<p>Update: wait time down to 19 minutes. Trending toward target.</p>', bodyText: 'Wait time down to 19 minutes.', attachments: [] },
    ],
    threadMessageCount: 4,
  },
  {
    id: 'em-005',
    threadId: 'thread-005',
    from: { name: 'Patient Relations', email: 'pr@sunridgeclinic.org' },
    to: [{ name: 'Practice Manager', email: 'pm@sunridgeclinic.org' }],
    cc: [],
    subject: 'Patient Complaint — Long Hold Times on Phone',
    preview: 'Received 3 complaints this week about hold times exceeding 10 minutes. Google review also posted.',
    bodyHtml: '<p>Hi,</p><p>We\'ve received <strong>3 patient complaints</strong> this week about phone hold times exceeding 10 minutes. One patient also posted a 2-star Google review mentioning the issue.</p><p>The complaints are concentrated during the 8-9am window when call volume peaks. Current staffing: 2 agents (capacity: ~30 calls/hr, actual volume: ~55).</p><p>Suggested action: add a third agent during peak hour or enable callback queue feature.</p>',
    bodyText: 'Received 3 complaints this week about hold times exceeding 10 minutes.',
    attachments: [],
    receivedAt: daysAgo(2),
    isRead: true,
    isStarred: false,
    folder: 'inbox',
    labels: [],
    primaryDomain: 'consumer',
    domains: [{ domain: 'consumer', confidence: 91 }, { domain: 'access', confidence: 35 }],
    urgency: 3,
    classificationConfidence: 91,
    entities: [
      { type: 'amount', value: '3 complaints', span: [25, 37] },
      { type: 'amount', value: '10 minutes', span: [55, 65] },
    ],
    threadSummary: '3 patient complaints about 10+ min phone hold times during 8-9am peak. 2-star Google review posted.',
    queueStatus: 'queued',
    priority: 'medium',
    scheduledDate: null,
    boardSwimlane: 'today',
    reclassifiedDomain: null,
    threadMessages: [
      { id: 'msg-005a', from: { name: 'Patient Relations', email: 'pr@sunridgeclinic.org' }, sentAt: daysAgo(2), bodyHtml: '<p>We\'ve received 3 patient complaints...</p>', bodyText: '3 complaints about phone hold times.', attachments: [] },
    ],
    threadMessageCount: 1,
  },
  {
    id: 'em-006',
    threadId: 'thread-006',
    from: { name: 'HR Department', email: 'hr@sunridgeclinic.org' },
    to: [{ name: 'Practice Manager', email: 'pm@sunridgeclinic.org' }],
    cc: [],
    subject: 'Credentialing Renewal — Dr. Martinez Expiring June 1',
    preview: 'Dr. Martinez\'s credentialing expires June 1. Documentation package due to medical board by May 15.',
    bodyHtml: '<p>Please be advised that Dr. Martinez\'s credentialing is set to expire on <strong>June 1, 2026</strong>. The documentation package must be submitted to the state medical board no later than <strong>May 15</strong>.</p><p>Required documents:</p><ul><li>Updated CV</li><li>CME certificates (last 24 months)</li><li>Malpractice insurance verification</li><li>Peer review letters (2 required)</li></ul><p>Please coordinate with Dr. Martinez to gather these documents.</p>',
    bodyText: 'Dr. Martinez credentialing expires June 1. Docs due May 15.',
    attachments: [],
    receivedAt: daysAgo(5),
    isRead: true,
    isStarred: false,
    folder: 'inbox',
    labels: [],
    primaryDomain: 'staffing',
    domains: [{ domain: 'staffing', confidence: 82 }, { domain: 'quality', confidence: 30 }],
    urgency: 2,
    classificationConfidence: 82,
    entities: [
      { type: 'person', value: 'Dr. Martinez', span: [30, 42] },
      { type: 'date', value: 'June 1, 2026', span: [65, 77] },
      { type: 'date', value: 'May 15', span: [120, 126] },
    ],
    threadSummary: 'Dr. Martinez credentialing renewal: expires June 1, docs due May 15. Need CV, CME certs, insurance, peer reviews.',
    queueStatus: 'scheduled',
    priority: 'medium',
    scheduledDate: '2026-05-01',
    boardSwimlane: 'scheduled',
    reclassifiedDomain: null,
    threadMessages: [
      { id: 'msg-006a', from: { name: 'HR Department', email: 'hr@sunridgeclinic.org' }, sentAt: daysAgo(5), bodyHtml: '<p>Dr. Martinez credentialing expires June 1...</p>', bodyText: 'Dr. Martinez credentialing expires June 1.', attachments: [] },
    ],
    threadMessageCount: 1,
  },
  {
    id: 'em-007',
    threadId: 'thread-007',
    from: { name: 'Scheduling System', email: 'noreply@schedulesync.com' },
    to: [{ name: 'Practice Manager', email: 'pm@sunridgeclinic.org' }],
    cc: [],
    subject: 'Daily Scheduling Alert — 8 No-Shows Today',
    preview: 'Today\'s no-show count: 8 (9.4% rate). 3 were new patient appointments worth ~$450 each.',
    bodyHtml: '<p><strong>Scheduling Summary — Today</strong></p><p>No-show count: <strong>8</strong> (rate: 9.4%)</p><p>Notable: 3 were new patient appointments (~$450 each in lost revenue). 2 were follow-up visits for chronic conditions.</p><p>Patterns: 5 of 8 no-shows were in the 2-4pm afternoon block.</p>',
    bodyText: 'Today\'s no-show count: 8 (9.4% rate). 3 new patient appointments worth ~$450 each.',
    attachments: [],
    receivedAt: hoursAgo(5),
    isRead: true,
    isStarred: false,
    folder: 'inbox',
    labels: [],
    primaryDomain: 'access',
    domains: [{ domain: 'access', confidence: 88 }, { domain: 'finance', confidence: 40 }],
    urgency: 2,
    classificationConfidence: 88,
    entities: [
      { type: 'amount', value: '8 no-shows', span: [20, 30] },
      { type: 'amount', value: '$450', span: [80, 84] },
    ],
    threadSummary: '8 no-shows today (9.4%). 3 new patient slots lost ($1,350). Afternoon block most affected.',
    queueStatus: 'none',
    priority: 'low',
    scheduledDate: null,
    boardSwimlane: null,
    reclassifiedDomain: null,
    threadMessages: [
      { id: 'msg-007a', from: { name: 'Scheduling System', email: 'noreply@schedulesync.com' }, sentAt: hoursAgo(5), bodyHtml: '<p>Scheduling Summary — Today. No-show count: 8...</p>', bodyText: '8 no-shows today.', attachments: [] },
    ],
    threadMessageCount: 1,
  },
  {
    id: 'em-008',
    threadId: 'thread-008',
    from: { name: 'Compliance Officer', email: 'compliance@sunridgeclinic.org' },
    to: [{ name: 'All Managers', email: 'managers@sunridgeclinic.org' }],
    cc: [],
    subject: 'HIPAA Training Deadline Reminder — April 30',
    preview: 'Annual HIPAA training must be completed by April 30. 12 staff members have not yet started.',
    bodyHtml: '<p>Reminder: Annual HIPAA training must be completed by <strong>April 30, 2026</strong>.</p><p>Status: <strong>12 staff members</strong> have not yet started the training module. 8 are in progress.</p><p>Non-compliance will result in audit findings and potential penalties. Please ensure your teams complete the training this week.</p>',
    bodyText: 'Annual HIPAA training deadline April 30. 12 staff members haven\'t started.',
    attachments: [],
    receivedAt: daysAgo(3),
    isRead: true,
    isStarred: false,
    folder: 'inbox',
    labels: [],
    primaryDomain: 'quality',
    domains: [{ domain: 'quality', confidence: 85 }],
    urgency: 3,
    classificationConfidence: 85,
    entities: [
      { type: 'date', value: 'April 30, 2026', span: [50, 64] },
      { type: 'amount', value: '12 staff', span: [75, 83] },
    ],
    threadSummary: 'HIPAA training deadline: April 30. 12 staff haven\'t started. Compliance risk if not completed.',
    queueStatus: 'queued',
    priority: 'high',
    scheduledDate: null,
    boardSwimlane: 'today',
    reclassifiedDomain: null,
    threadMessages: [
      { id: 'msg-008a', from: { name: 'Compliance Officer', email: 'compliance@sunridgeclinic.org' }, sentAt: daysAgo(3), bodyHtml: '<p>Annual HIPAA training must be completed by April 30...</p>', bodyText: 'HIPAA training deadline April 30.', attachments: [] },
    ],
    threadMessageCount: 1,
  },
  {
    id: 'em-009',
    threadId: 'thread-009',
    from: { name: 'Supply Chain Portal', email: 'orders@medisupply.com' },
    to: [{ name: 'Practice Manager', email: 'pm@sunridgeclinic.org' }],
    cc: [],
    subject: 'Backorder Alert: Flu Vaccine Supply Delayed',
    preview: 'Your order #MED-7723 for 200 flu vaccine doses is backordered. Estimated delivery: 2-3 weeks.',
    bodyHtml: '<p>Order <strong>#MED-7723</strong> status update:</p><p>200 doses of influenza vaccine (Fluzone Quad) are currently <strong>backordered</strong> due to manufacturer supply constraints. Estimated delivery: 2-3 weeks.</p><p>Alternative: We have 150 doses of FluMist (nasal spray) available for immediate shipment. Would you like to proceed with the alternative?</p>',
    bodyText: 'Order #MED-7723 backordered. 200 flu vaccine doses delayed 2-3 weeks.',
    attachments: [],
    receivedAt: daysAgo(1),
    isRead: false,
    isStarred: false,
    folder: 'inbox',
    labels: [],
    primaryDomain: 'quality',
    domains: [{ domain: 'quality', confidence: 55 }, { domain: 'finance', confidence: 30 }, { domain: 'access', confidence: 20 }],
    urgency: 2,
    classificationConfidence: 55,
    entities: [
      { type: 'amount', value: '200 doses', span: [30, 39] },
      { type: 'amount', value: '150 doses', span: [120, 129] },
    ],
    threadSummary: 'Flu vaccine order backordered 2-3 weeks. 150 doses of alternative (FluMist) available now.',
    queueStatus: 'none',
    priority: 'medium',
    scheduledDate: null,
    boardSwimlane: null,
    reclassifiedDomain: null,
    threadMessages: [
      { id: 'msg-009a', from: { name: 'Supply Chain Portal', email: 'orders@medisupply.com' }, sentAt: daysAgo(1), bodyHtml: '<p>Order #MED-7723 status update...</p>', bodyText: 'Order backordered.', attachments: [] },
    ],
    threadMessageCount: 1,
  },
  {
    id: 'em-010',
    threadId: 'thread-010',
    from: { name: 'Outreach Coordinator', email: 'outreach@sunridgeclinic.org' },
    to: [{ name: 'Practice Manager', email: 'pm@sunridgeclinic.org' }],
    cc: [],
    subject: 'Population Health — 24 High-Risk Patients Overdue',
    preview: '24 high-risk patients haven\'t been seen in 90+ days. 8 are diabetic with overdue A1C labs.',
    bodyHtml: '<p>Weekly high-risk patient report:</p><p><strong>24 patients</strong> classified as high-risk have not had a visit in 90+ days:</p><ul><li>8 diabetic patients with overdue A1C labs</li><li>6 patients with uncontrolled hypertension</li><li>5 post-surgical follow-ups overdue</li><li>5 patients with behavioral health referrals pending</li></ul><p>Outreach calls scheduled for this week. Phone trees attached.</p>',
    bodyText: '24 high-risk patients overdue 90+ days. 8 diabetic with overdue A1C.',
    attachments: [{ id: 'att-010', filename: 'outreach-phone-tree.pdf', mimeType: 'application/pdf', size: 85000 }],
    receivedAt: daysAgo(2),
    isRead: false,
    isStarred: false,
    folder: 'inbox',
    labels: [],
    primaryDomain: 'health',
    domains: [{ domain: 'health', confidence: 93 }, { domain: 'quality', confidence: 42 }],
    urgency: 3,
    classificationConfidence: 93,
    entities: [
      { type: 'amount', value: '24 patients', span: [30, 41] },
      { type: 'amount', value: '8 diabetic', span: [70, 80] },
    ],
    threadSummary: '24 high-risk patients overdue (90+ days). Breakdown: 8 diabetic, 6 hypertension, 5 post-surgical, 5 behavioral health.',
    queueStatus: 'none',
    priority: 'high',
    scheduledDate: null,
    boardSwimlane: null,
    reclassifiedDomain: null,
    threadMessages: [
      { id: 'msg-010a', from: { name: 'Outreach Coordinator', email: 'outreach@sunridgeclinic.org' }, sentAt: daysAgo(2), bodyHtml: '<p>Weekly high-risk patient report...</p>', bodyText: '24 high-risk patients overdue.', attachments: [{ id: 'att-010', filename: 'outreach-phone-tree.pdf', mimeType: 'application/pdf', size: 85000 }] },
    ],
    threadMessageCount: 1,
  },
  {
    id: 'em-011',
    threadId: 'thread-011',
    from: { name: 'IT Help Desk', email: 'it@sunridgeclinic.org' },
    to: [{ name: 'All Staff', email: 'staff@sunridgeclinic.org' }],
    cc: [],
    subject: 'Scheduled Maintenance — EHR Downtime Saturday 2-4am',
    preview: 'EHR system will be offline Saturday 2-4am for security patches. No action needed.',
    bodyHtml: '<p>Planned maintenance window: <strong>Saturday 2:00 AM – 4:00 AM</strong></p><p>The EHR system will be offline for security patches. All data will be preserved. No action needed from clinical staff.</p><p>Emergency access via the backup read-only portal will remain available.</p>',
    bodyText: 'EHR downtime Saturday 2-4am for security patches.',
    attachments: [],
    receivedAt: daysAgo(1),
    isRead: true,
    isStarred: false,
    folder: 'inbox',
    labels: [],
    primaryDomain: 'quality',
    domains: [{ domain: 'quality', confidence: 48 }, { domain: 'access', confidence: 38 }],
    urgency: 1,
    classificationConfidence: 48,
    entities: [
      { type: 'date', value: 'Saturday 2-4am', span: [25, 40] },
    ],
    threadSummary: 'Routine EHR maintenance Saturday 2-4am. No clinical impact expected.',
    queueStatus: 'none',
    priority: 'low',
    scheduledDate: null,
    boardSwimlane: null,
    reclassifiedDomain: null,
    threadMessages: [
      { id: 'msg-011a', from: { name: 'IT Help Desk', email: 'it@sunridgeclinic.org' }, sentAt: daysAgo(1), bodyHtml: '<p>Planned maintenance Saturday 2-4am...</p>', bodyText: 'EHR downtime Saturday 2-4am.', attachments: [] },
    ],
    threadMessageCount: 1,
  },
  {
    id: 'em-012',
    threadId: 'thread-012',
    from: { name: 'Regional Director', email: 'director@healthsys.org' },
    to: [{ name: 'Practice Manager', email: 'pm@sunridgeclinic.org' }],
    cc: [],
    subject: 'Monthly P&L Review — Revenue Up 3%, Denials Watch',
    preview: 'March P&L: revenue up 3% MoM but denial rate climbed to 6.2%. Need action plan by Friday.',
    bodyHtml: '<p>March P&L highlights:</p><ul><li>Revenue: <strong>+3% MoM</strong> ($312/patient avg)</li><li>Collections rate: 88% (target: 90%)</li><li>Denial rate: <strong>6.2%</strong> (up from 4.8%) — needs action plan</li><li>Days in AR: 32 days (stable)</li></ul><p>Please prepare a denial reduction action plan by <strong>Friday EOD</strong>. Focus on prior auth and coding accuracy.</p>',
    bodyText: 'March P&L: revenue +3%, denial rate 6.2% needs action plan by Friday.',
    attachments: [{ id: 'att-012', filename: 'March-PL-Summary.xlsx', mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', size: 156000 }],
    receivedAt: daysAgo(2),
    isRead: true,
    isStarred: true,
    folder: 'inbox',
    labels: ['follow-up'],
    primaryDomain: 'finance',
    domains: [{ domain: 'finance', confidence: 98 }],
    urgency: 3,
    classificationConfidence: 98,
    entities: [
      { type: 'amount', value: '6.2%', span: [90, 94] },
      { type: 'amount', value: '$312/patient', span: [45, 57] },
      { type: 'date', value: 'Friday EOD', span: [180, 190] },
    ],
    threadSummary: 'March P&L: revenue +3%, but denial rate up to 6.2%. Director wants denial reduction plan by Friday.',
    queueStatus: 'queued',
    priority: 'high',
    scheduledDate: null,
    boardSwimlane: 'today',
    reclassifiedDomain: null,
    threadMessages: [
      { id: 'msg-012a', from: { name: 'Regional Director', email: 'director@healthsys.org' }, sentAt: daysAgo(2), bodyHtml: '<p>March P&L highlights...</p>', bodyText: 'March P&L: revenue +3%, denials 6.2%.', attachments: [{ id: 'att-012', filename: 'March-PL-Summary.xlsx', mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', size: 156000 }] },
    ],
    threadMessageCount: 1,
  },
  {
    id: 'em-013',
    threadId: 'thread-013',
    from: { name: 'Referral Coordinator', email: 'referrals@sunridgeclinic.org' },
    to: [{ name: 'Practice Manager', email: 'pm@sunridgeclinic.org' }],
    cc: [],
    subject: 'Referral Backlog — 28 Pending > 14 Days',
    preview: '28 referrals pending over 14 days. Cardiology (9) and Endocrinology (7) are the bottlenecks.',
    bodyHtml: '<p>Referral backlog update:</p><p><strong>28 referrals</strong> have been pending for more than 14 days:</p><ul><li>Cardiology: 9 referrals (avg wait: 22 days)</li><li>Endocrinology: 7 referrals (avg wait: 18 days)</li><li>Orthopedics: 6 referrals (avg wait: 16 days)</li><li>Other: 6 referrals</li></ul><p>I\'ve escalated cardiology and endo to their practice coordinators. Should see movement this week.</p>',
    bodyText: '28 referrals pending over 14 days. Cardiology and Endocrinology bottlenecks.',
    attachments: [],
    receivedAt: daysAgo(1),
    isRead: false,
    isStarred: false,
    folder: 'inbox',
    labels: [],
    primaryDomain: 'access',
    domains: [{ domain: 'access', confidence: 90 }, { domain: 'quality', confidence: 25 }],
    urgency: 2,
    classificationConfidence: 90,
    entities: [
      { type: 'amount', value: '28 referrals', span: [20, 32] },
      { type: 'amount', value: '9 referrals', span: [70, 81] },
    ],
    threadSummary: '28 referrals pending 14+ days. Cardiology (9) and Endocrinology (7) are worst bottlenecks.',
    queueStatus: 'none',
    priority: 'medium',
    scheduledDate: null,
    boardSwimlane: null,
    reclassifiedDomain: null,
    threadMessages: [
      { id: 'msg-013a', from: { name: 'Referral Coordinator', email: 'referrals@sunridgeclinic.org' }, sentAt: daysAgo(1), bodyHtml: '<p>Referral backlog: 28 pending 14+ days...</p>', bodyText: '28 referrals pending.', attachments: [] },
    ],
    threadMessageCount: 1,
  },
  {
    id: 'em-014',
    threadId: 'thread-014',
    from: { name: 'NexoNurse System', email: 'noreply@nexonurse.com' },
    to: [{ name: 'Practice Manager', email: 'pm@sunridgeclinic.org' }],
    cc: [],
    subject: 'Weekly Huddle Brief — Week of April 21',
    preview: 'Your AI-generated weekly brief is ready. 3 critical items, 5 action items, 2 wins to celebrate.',
    bodyHtml: '<p><strong>Weekly Huddle Brief</strong></p><p>Critical items: 3 | Action items: 5 | Wins: 2</p><p>Top priority: Weekend staffing gap (2 RN positions). Second: Patient wait time resolution follow-up. Third: HIPAA training compliance deadline.</p><p>Wins: Revenue up 3% MoM. Referral backlog improving (down from 34 to 28).</p>',
    bodyText: 'Weekly huddle brief ready. 3 critical items, 5 action items, 2 wins.',
    attachments: [],
    receivedAt: hoursAgo(6),
    isRead: true,
    isStarred: false,
    folder: 'inbox',
    labels: [],
    primaryDomain: 'staffing',
    domains: [{ domain: 'staffing', confidence: 35 }, { domain: 'quality', confidence: 30 }, { domain: 'access', confidence: 25 }],
    urgency: 1,
    classificationConfidence: 35,
    entities: [],
    threadSummary: 'Weekly AI brief: 3 critical items (staffing, wait times, HIPAA), 5 action items, 2 wins.',
    queueStatus: 'none',
    priority: 'low',
    scheduledDate: null,
    boardSwimlane: null,
    reclassifiedDomain: null,
    threadMessages: [
      { id: 'msg-014a', from: { name: 'NexoNurse System', email: 'noreply@nexonurse.com' }, sentAt: hoursAgo(6), bodyHtml: '<p>Weekly Huddle Brief...</p>', bodyText: 'Weekly brief ready.', attachments: [] },
    ],
    threadMessageCount: 1,
  },
  // ── Thread replies (share threadId to demonstrate multi-email topic groups) ──
  {
    id: 'em-019',
    threadId: 'thread-001',
    from: { name: 'HR Department', email: 'hr@sunridgeclinic.org' },
    to: [{ name: 'Dr. Sarah Smith', email: 'sarah.smith@sunridgeclinic.org' }],
    cc: [{ name: 'Practice Manager', email: 'pm@sunridgeclinic.org' }],
    subject: 'RE: Staffing Coverage — Weekend Shifts',
    preview: 'Float pool confirmed — Maria Chen and James Liu available for Saturday. Sunday still has 1 gap.',
    bodyHtml: '<p>Hi Sarah,</p><p>Float pool update: <strong>Maria Chen</strong> and <strong>James Liu</strong> are confirmed for Saturday (day shift). Sunday still has 1 RN gap — reaching out to agency staffing now.</p><p>Will update by 3 PM today.</p><p>— HR</p>',
    bodyText: 'Float pool confirmed — Maria Chen and James Liu available for Saturday. Sunday still has 1 gap.',
    attachments: [],
    receivedAt: hoursAgo(1),
    isRead: false,
    isStarred: false,
    folder: 'inbox',
    labels: ['follow-up'],
    primaryDomain: 'staffing',
    domains: [{ domain: 'staffing', confidence: 96 }],
    urgency: 4,
    classificationConfidence: 96,
    entities: [
      { type: 'person', value: 'Maria Chen', span: [30, 40] },
      { type: 'person', value: 'James Liu', span: [45, 54] },
    ],
    threadSummary: 'Float pool partially confirmed: 2 RNs for Saturday, 1 Sunday gap remains. Agency backup in progress.',
    queueStatus: 'none',
    priority: 'high',
    scheduledDate: null,
    boardSwimlane: null,
    reclassifiedDomain: null,
    threadMessages: [
      { id: 'msg-019a', from: { name: 'HR Department', email: 'hr@sunridgeclinic.org' }, sentAt: hoursAgo(1), bodyHtml: '<p>Float pool confirmed — Maria Chen and James Liu for Saturday...</p>', bodyText: 'Float pool confirmed.', attachments: [] },
    ],
    threadMessageCount: 1,
  },
  {
    id: 'em-020',
    threadId: 'thread-004',
    from: { name: 'Dr. Patel', email: 'patel@sunridgeclinic.org' },
    to: [{ name: 'Access Team', email: 'access@sunridgeclinic.org' }],
    cc: [{ name: 'Practice Manager', email: 'pm@sunridgeclinic.org' }],
    subject: 'RE: URGENT: Patient Wait Times Exceeding Target',
    preview: 'Room C helped — my queue is down to 8 min wait. Can take 3 more walk-ins before noon.',
    bodyHtml: '<p>Update from my side:</p><p>Room C redirect is working. My current wait: <strong>8 minutes</strong>. I can take <strong>3 more walk-ins</strong> before noon.</p><p>After noon I have scheduled patients back-to-back, so redirects should go to Dr. Chen instead.</p>',
    bodyText: 'Room C helped — my queue is down to 8 min wait. Can take 3 more walk-ins before noon.',
    attachments: [],
    receivedAt: hoursAgo(0.5),
    isRead: false,
    isStarred: false,
    folder: 'inbox',
    labels: [],
    primaryDomain: 'access',
    domains: [{ domain: 'access', confidence: 95 }, { domain: 'staffing', confidence: 15 }],
    urgency: 4,
    classificationConfidence: 95,
    entities: [
      { type: 'amount', value: '8 minutes', span: [50, 59] },
      { type: 'person', value: 'Dr. Chen', span: [130, 138] },
    ],
    threadSummary: 'Dr. Patel reports wait down to 8 min after Room C redirect. Can take 3 more walk-ins before noon, then redirect to Dr. Chen.',
    queueStatus: 'none',
    priority: 'high',
    scheduledDate: null,
    boardSwimlane: null,
    reclassifiedDomain: null,
    threadMessages: [
      { id: 'msg-020a', from: { name: 'Dr. Patel', email: 'patel@sunridgeclinic.org' }, sentAt: hoursAgo(0.5), bodyHtml: '<p>Room C redirect working. Wait: 8 min...</p>', bodyText: 'Room C helped.', attachments: [] },
    ],
    threadMessageCount: 1,
  },
  {
    id: 'em-021',
    threadId: 'thread-004',
    from: { name: 'Front Desk', email: 'frontdesk@sunridgeclinic.org' },
    to: [{ name: 'Access Team', email: 'access@sunridgeclinic.org' }],
    cc: [{ name: 'Practice Manager', email: 'pm@sunridgeclinic.org' }],
    subject: 'RE: URGENT: Patient Wait Times Exceeding Target',
    preview: 'All clear — current average wait is 12 min. Room C still open. Julie replacement arriving at 11.',
    bodyHtml: '<p><strong>Status: IMPROVING</strong></p><p>Current average wait: <strong>12 minutes</strong> (down from 42).</p><p>Room C still open. Walk-in queue cleared. Julie\'s replacement (temp) arriving at 11 AM.</p><p>Recommend keeping Room C open through lunch to prevent afternoon spike.</p>',
    bodyText: 'All clear — current average wait is 12 min. Room C still open.',
    attachments: [],
    receivedAt: hoursAgo(0.25),
    isRead: false,
    isStarred: false,
    folder: 'inbox',
    labels: [],
    primaryDomain: 'access',
    domains: [{ domain: 'access', confidence: 97 }],
    urgency: 3,
    classificationConfidence: 97,
    entities: [
      { type: 'amount', value: '12 minutes', span: [35, 45] },
    ],
    threadSummary: 'Wait times recovering — 12 min avg (was 42). Room C open, temp staff arriving at 11 AM.',
    queueStatus: 'none',
    priority: 'medium',
    scheduledDate: null,
    boardSwimlane: null,
    reclassifiedDomain: null,
    threadMessages: [
      { id: 'msg-021a', from: { name: 'Front Desk', email: 'frontdesk@sunridgeclinic.org' }, sentAt: hoursAgo(0.25), bodyHtml: '<p>Status: IMPROVING. Wait: 12 min...</p>', bodyText: 'All clear — 12 min wait.', attachments: [] },
    ],
    threadMessageCount: 1,
  },
  // ── Sent / Drafts / Archive samples ─────────────────────────────────────────
  {
    id: 'em-015',
    threadId: 'thread-015',
    from: { name: 'Practice Manager', email: 'pm@sunridgeclinic.org' },
    to: [{ name: 'HR Department', email: 'hr@sunridgeclinic.org' }],
    cc: [],
    subject: 'Float Pool Request — Weekend Coverage',
    preview: 'Requesting 2 RNs from the float pool for Saturday and Sunday shifts.',
    bodyHtml: '<p>Hi HR,</p><p>Please confirm availability of 2 float pool RNs for this weekend (Sat/Sun). Day shift preferred. This is urgent — please respond by EOD Thursday.</p><p>Thanks.</p>',
    bodyText: 'Requesting 2 float pool RNs for this weekend.',
    attachments: [],
    receivedAt: hoursAgo(2.5),
    isRead: true,
    isStarred: false,
    folder: 'sent',
    labels: [],
    primaryDomain: 'staffing',
    domains: [{ domain: 'staffing', confidence: 95 }],
    urgency: 4,
    classificationConfidence: 95,
    entities: [],
    threadSummary: null,
    queueStatus: 'none',
    priority: 'high',
    scheduledDate: null,
    boardSwimlane: null,
    reclassifiedDomain: null,
    threadMessages: [
      { id: 'msg-015a', from: { name: 'Practice Manager', email: 'pm@sunridgeclinic.org' }, sentAt: hoursAgo(2.5), bodyHtml: '<p>Requesting 2 float pool RNs...</p>', bodyText: 'Requesting 2 float pool RNs.', attachments: [] },
    ],
    threadMessageCount: 1,
  },
  {
    id: 'em-016',
    threadId: 'thread-016',
    from: { name: 'Practice Manager', email: 'pm@sunridgeclinic.org' },
    to: [{ name: 'Regional Director', email: 'director@healthsys.org' }],
    cc: [],
    subject: 'Draft: Denial Reduction Action Plan',
    preview: 'Working on the denial reduction plan. Key strategies: prior auth automation, coding audit...',
    bodyHtml: '<p><em>DRAFT — Not yet sent</em></p><p>Denial Reduction Plan:</p><ol><li>Implement prior auth automation (est. reduce denials by 40%)</li><li>Weekly coding accuracy audit</li><li>Payer-specific denial pattern analysis</li></ol>',
    bodyText: 'Draft denial reduction plan.',
    attachments: [],
    receivedAt: hoursAgo(1),
    isRead: true,
    isStarred: false,
    folder: 'drafts',
    labels: [],
    primaryDomain: 'finance',
    domains: [{ domain: 'finance', confidence: 96 }],
    urgency: 3,
    classificationConfidence: 96,
    entities: [],
    threadSummary: null,
    queueStatus: 'none',
    priority: 'high',
    scheduledDate: null,
    boardSwimlane: null,
    reclassifiedDomain: null,
    threadMessages: [
      { id: 'msg-016a', from: { name: 'Practice Manager', email: 'pm@sunridgeclinic.org' }, sentAt: hoursAgo(1), bodyHtml: '<p>Denial Reduction Plan draft...</p>', bodyText: 'Draft denial reduction plan.', attachments: [] },
    ],
    threadMessageCount: 1,
  },
  {
    id: 'em-017',
    threadId: 'thread-017',
    from: { name: 'Medical Equipment Co.', email: 'service@medequip.com' },
    to: [{ name: 'Practice Manager', email: 'pm@sunridgeclinic.org' }],
    cc: [],
    subject: 'Service Complete — Exam Room B Equipment Calibration',
    preview: 'Equipment calibration in Exam Room B is complete. All devices passed inspection.',
    bodyHtml: '<p>Service ticket <strong>#SVC-1189</strong> is now closed.</p><p>All equipment in Exam Room B has been calibrated and passed inspection. Next scheduled maintenance: July 2026.</p>',
    bodyText: 'Exam Room B calibration complete. Next maintenance July 2026.',
    attachments: [{ id: 'att-017', filename: 'calibration-report.pdf', mimeType: 'application/pdf', size: 42000 }],
    receivedAt: daysAgo(7),
    isRead: true,
    isStarred: false,
    folder: 'archive',
    labels: [],
    primaryDomain: 'quality',
    domains: [{ domain: 'quality', confidence: 72 }],
    urgency: 1,
    classificationConfidence: 72,
    entities: [
      { type: 'date', value: 'July 2026', span: [90, 99] },
    ],
    threadSummary: 'Exam Room B equipment calibration complete. Next service: July 2026.',
    queueStatus: 'completed',
    priority: 'low',
    scheduledDate: null,
    boardSwimlane: null,
    reclassifiedDomain: null,
    threadMessages: [
      { id: 'msg-017a', from: { name: 'Medical Equipment Co.', email: 'service@medequip.com' }, sentAt: daysAgo(7), bodyHtml: '<p>Service ticket #SVC-1189 closed...</p>', bodyText: 'Calibration complete.', attachments: [{ id: 'att-017', filename: 'calibration-report.pdf', mimeType: 'application/pdf', size: 42000 }] },
    ],
    threadMessageCount: 1,
  },
  {
    id: 'em-018',
    threadId: 'thread-018',
    from: { name: 'State Health Dept', email: 'notifications@statehealth.gov' },
    to: [{ name: 'Practice Manager', email: 'pm@sunridgeclinic.org' }],
    cc: [],
    subject: 'Immunization Registry Update — New Reporting Requirements',
    preview: 'New state immunization reporting requirements effective July 1. COVID + flu must be reported within 24 hrs.',
    bodyHtml: '<p>Effective <strong>July 1, 2026</strong>, all healthcare facilities must report COVID-19 and influenza vaccinations to the state immunization registry within <strong>24 hours</strong> of administration.</p><p>Current reporting window: 72 hours. Please update your workflows accordingly.</p><p>Non-compliance may result in facility sanctions.</p>',
    bodyText: 'New immunization reporting: COVID + flu within 24 hours effective July 1.',
    attachments: [],
    receivedAt: daysAgo(6),
    isRead: true,
    isStarred: false,
    folder: 'inbox',
    labels: [],
    primaryDomain: 'quality',
    domains: [{ domain: 'quality', confidence: 80 }, { domain: 'health', confidence: 45 }],
    urgency: 2,
    classificationConfidence: 80,
    entities: [
      { type: 'date', value: 'July 1, 2026', span: [10, 22] },
    ],
    threadSummary: 'New state rule: report COVID/flu vaccines within 24hrs (was 72hrs). Effective July 1.',
    queueStatus: 'scheduled',
    priority: 'medium',
    scheduledDate: '2026-06-15',
    boardSwimlane: 'scheduled',
    reclassifiedDomain: null,
    threadMessages: [
      { id: 'msg-018a', from: { name: 'State Health Dept', email: 'notifications@statehealth.gov' }, sentAt: daysAgo(6), bodyHtml: '<p>New reporting requirements effective July 1...</p>', bodyText: 'New immunization reporting requirements.', attachments: [] },
    ],
    threadMessageCount: 1,
  },
];

/** AI-generated domain-level summaries (mock) */
export const DOMAIN_SUMMARIES = {
  staffing: 'Weekend coverage gap: 2 RN positions unfilled for Sat-Sun. Float pool request pending HR confirmation (deadline: Friday 5pm). Dr. Martinez credentialing renewal due June 1 — documentation package due May 15. Weekly huddle flagged staffing as top priority.',
  quality: 'Q1 A1C control rate dropped to 68% (target 75%), requiring outreach to 47 patients. HIPAA training deadline April 30 with 12 staff not yet started. Flu vaccine supply backordered 2-3 weeks — alternative available. EHR maintenance scheduled Saturday 2-4am. New immunization reporting rules effective July 1.',
  finance: 'Claim #4821-RX denied for missing prior auth ($285). March P&L shows revenue up 3% but denial rate climbed to 6.2% — director wants reduction plan by Friday. Draft denial reduction plan in progress.',
  access: 'URGENT: Wait times hit 42 minutes (target 15 min). Overflow room opened, trending back toward target. 8 no-shows today (9.4% rate, $1,350 in lost new-patient revenue). 28 referrals backlogged 14+ days — cardiology and endocrinology worst.',
  consumer: '3 patient complaints about phone hold times exceeding 10 minutes during 8-9am peak. 2-star Google review posted. Current staffing: 2 agents handling 55 calls/hr (capacity: 30). Suggested: add third agent or enable callback queue.',
  health: '24 high-risk patients not seen in 90+ days: 8 diabetic (overdue A1C), 6 uncontrolled hypertension, 5 post-surgical, 5 behavioral health referrals pending. Outreach calls scheduled this week.',
};

/** Domain labels for display */
export const DOMAIN_LABELS = {
  staffing: 'Staffing',
  quality: 'Quality',
  access: 'Access',
  finance: 'Finance',
  consumer: 'Consumer',
  health: 'Pop Health',
};

/** All available folders */
export const FOLDERS = [
  { key: 'inbox',   label: 'Inbox',   icon: 'Inbox' },
  { key: 'sent',    label: 'Sent',    icon: 'Send' },
  { key: 'drafts',  label: 'Drafts',  icon: 'FileEdit' },
  { key: 'archive', label: 'Archive', icon: 'Archive' },
  { key: 'trash',   label: 'Trash',   icon: 'Trash2' },
];
